/**
 * Traffic Source Table Component
 *
 * Detailed table showing all traffic source metrics
 */

import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCurrency } from "@/domains/customer/customers/utils/customer-utils";
import type { TrafficSourceData } from "../types/web-types";
import { MetricTooltip } from "./metric-tooltip";

interface TrafficSourceTableProps {
  current: TrafficSourceData[];
  previous: TrafficSourceData[];
}

export function TrafficSourceTable({ current, previous }: TrafficSourceTableProps) {
  const { t } = useTranslation("reports");

  // Combine current and previous data
  const tableData = current.map((curr) => {
    const prev = previous.find((p) => p.sourceId === curr.sourceId);
    return {
      source: curr.sourceName,
      currentInquiries: curr.inquiries,
      previousInquiries: prev?.inquiries || 0,
      currentBookings: curr.bookings,
      previousBookings: prev?.bookings || 0,
      currentConversionRate: curr.conversionRate,
      previousConversionRate: prev?.conversionRate || 0,
      currentRevenue: curr.revenue,
      previousRevenue: prev?.revenue || 0,
    };
  });

  const calculateChange = (current: number, previous: number) => {
    if (previous === 0) return null;
    const change = ((current - previous) / previous) * 100;
    return change;
  };

  const renderChangeIndicator = (change: number | null) => {
    if (change === null) return null;
    const isPositive = change > 0;
    const colorClass = isPositive
      ? "text-green-600"
      : change < 0
        ? "text-red-600"
        : "text-muted-foreground";
    return (
      <span className={`text-xs ${colorClass}`}>
        ({isPositive ? "+" : ""}
        {change.toFixed(1)}%)
      </span>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center">
          {t("charts.trafficSources.title")}
          <MetricTooltip content={t("charts.trafficSources.tooltip")} />
        </CardTitle>
        <p className="text-xs text-muted-foreground">{t("charts.trafficSources.subtitle")}</p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b">
                <th className="text-left p-2 font-medium">{t("common.source")}</th>
                <th className="text-right p-2 font-medium">{t("common.inquiries")}</th>
                <th className="text-right p-2 font-medium">{t("common.bookings")}</th>
                <th className="text-right p-2 font-medium">{t("common.conversionRate")}</th>
                <th className="text-right p-2 font-medium">{t("common.revenue")}</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, idx) => (
                <tr key={idx} className="border-b hover:bg-muted/50">
                  <td className="p-2 font-medium">{row.source}</td>
                  <td className="p-2 text-right">
                    <div className="flex flex-col items-end">
                      <span>{row.currentInquiries}</span>
                      <span className="text-muted-foreground">
                        ({t("common.was")} {row.previousInquiries})
                      </span>
                      {renderChangeIndicator(
                        calculateChange(row.currentInquiries, row.previousInquiries)
                      )}
                    </div>
                  </td>
                  <td className="p-2 text-right">
                    <div className="flex flex-col items-end">
                      <span>{row.currentBookings}</span>
                      <span className="text-muted-foreground">
                        ({t("common.was")} {row.previousBookings})
                      </span>
                      {renderChangeIndicator(
                        calculateChange(row.currentBookings, row.previousBookings)
                      )}
                    </div>
                  </td>
                  <td className="p-2 text-right">
                    <div className="flex flex-col items-end">
                      <span>{row.currentConversionRate.toFixed(1)}%</span>
                      <span className="text-muted-foreground">
                        ({t("common.was")} {row.previousConversionRate.toFixed(1)}%)
                      </span>
                      {renderChangeIndicator(
                        calculateChange(row.currentConversionRate, row.previousConversionRate)
                      )}
                    </div>
                  </td>
                  <td className="p-2 text-right">
                    <div className="flex flex-col items-end">
                      <span>{formatCurrency(row.currentRevenue)}</span>
                      <span className="text-muted-foreground">
                        (was {formatCurrency(row.previousRevenue)})
                      </span>
                      {renderChangeIndicator(
                        calculateChange(row.currentRevenue, row.previousRevenue)
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
